import { useEffect, useId, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { api } from "./api";
import { ErrorNotice } from "./ui";
import "./google-sign-in.css";

const callbackMessages: Record<string, string> = {
  google_unavailable:
    "Google sign-in is unavailable right now. Try again later or sign in with your email and password.",
  google_rate_limited:
    "There have been too many Google sign-in attempts. Wait up to 15 minutes before trying again, or sign in with your email and password.",
  google_cancelled:
    "Google sign-in was cancelled. You can try again or sign in with your email and password.",
  google_invalid_response:
    "This Google sign-in attempt expired or could not be verified. Please start again.",
  google_email_unverified:
    "Google could not confirm your email address. Verify it with Google before trying again, or use email sign-in.",
  google_email_verification_required:
    "Verify your email with Sol Office, then try Google again. Check your inbox or use Resend verification below.",
  google_link_required:
    "This email already has a Sol Office account. Sign in with your existing password. Google has not been linked to that account.",
  google_account_unavailable:
    "This Sol Office account is unavailable. Contact your workspace administrator for help.",
  google_failed:
    "Google sign-in could not be completed. Please try again or sign in with your email and password.",
};

export function readGoogleAuthError() {
  const code = new URL(location.href).searchParams.get("authError");
  if (!code) return "";
  // Never render provider response text or arbitrary query-string content.
  return Object.hasOwn(callbackMessages, code)
    ? callbackMessages[code]!
    : callbackMessages.google_failed!;
}

export function GoogleSignIn({
  disabled,
  redirecting,
  onRedirecting,
  callbackError,
  clearCallbackError,
  invitationPending,
}: {
  disabled: boolean;
  redirecting: boolean;
  onRedirecting: (pending: boolean) => void;
  callbackError: string;
  clearCallbackError: () => void;
  invitationPending: boolean;
}) {
  const [availability, setAvailability] = useState<
    "loading" | "enabled" | "disabled" | "error"
  >("loading");
  const [revision, setRevision] = useState(0);
  const invitationId = useId();
  useEffect(() => {
    let active = true;
    const abort = new AbortController();
    setAvailability("loading");
    void api<{ enabled: boolean }>("/auth/google/config", {
      signal: abort.signal,
    })
      .then((result) => {
        if (!active) return;
        setAvailability(
          result?.enabled === true
            ? "enabled"
            : result?.enabled === false
              ? "disabled"
              : "error",
        );
      })
      .catch(() => {
        if (active) setAvailability("error");
      });
    return () => {
      active = false;
      abort.abort();
    };
  }, [revision]);
  useEffect(() => {
    // Returning from the provider with browser Back can restore this page from
    // the back/forward cache, including its previous redirecting state.
    const returned = () => onRedirecting(false);
    window.addEventListener("pageshow", returned);
    return () => window.removeEventListener("pageshow", returned);
  }, [onRedirecting]);
  const start = () => {
    if (disabled || redirecting || availability !== "enabled") return;
    clearCallbackError();
    onRedirecting(true);
    try {
      // The server owns state, PKCE, token exchange and session cookies. No
      // Google authorization token or invitation secret enters browser storage.
      location.assign("/api/v1/auth/google/start");
    } catch {
      onRedirecting(false);
      setAvailability("error");
    }
  };
  return (
    <div className="auth-google">
      {callbackError && (
        <div className="auth-google-error">
          <ErrorNotice message={callbackError} />
          <button
            type="button"
            className="text-button"
            onClick={clearCallbackError}
          >
            Dismiss sign-in message
          </button>
        </div>
      )}
      {availability === "enabled" ? (
        <>
          {invitationPending && (
            <p className="auth-google-status" id={invitationId}>
              After signing in with Google, reopen your invitation to join that
              workspace.
            </p>
          )}
          <button
            type="button"
            className="google-sign-in-button"
            disabled={disabled || redirecting}
            onClick={start}
            aria-describedby={invitationPending ? invitationId : undefined}
          >
            {redirecting ? (
              <LoaderCircle size={20} className="spin" aria-hidden="true" />
            ) : (
              // Official current Google Identity brand asset, unchanged:
              // https://developers.google.com/identity/branding-guidelines
              <img src="/brand/google-g.png" alt="" width={20} height={20} />
            )}
            <span>
              {redirecting ? "Opening Google…" : "Continue with Google"}
            </span>
          </button>
          <div className="auth-google-divider">or use your email</div>
        </>
      ) : availability === "loading" ? (
        <p className="auth-google-status" role="status">
          Checking Google sign-in…
        </p>
      ) : availability === "error" ? (
        <div className="auth-google-status" role="status">
          <p>
            Google sign-in could not be checked. Email sign-in is available.
          </p>
          <button
            type="button"
            className="text-button"
            disabled={disabled}
            onClick={() => setRevision((value) => value + 1)}
          >
            Check Google sign-in again
          </button>
        </div>
      ) : (
        <p className="auth-google-status">
          Google sign-in is unavailable. Use your email and password below.
        </p>
      )}
    </div>
  );
}
